/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-list/table.tsx
 * The state tiles above the table are
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx's solid
 * icon tiles, each a link into the list filtered to that state, counting with the API's own
 * `totalCount` — the same figures the State dropdown already shows.
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import type { ReactNode } from "react";
import {
  PiCheckCircleDuotone,
  PiListNumbersDuotone,
  PiPencilSimpleLineDuotone,
  PiReceiptDuotone,
  PiHourglassDuotone,
  PiReceiptXDuotone,
  PiScalesDuotone,
  PiSealCheckDuotone,
  PiWarningDuotone,
  PiXCircleDuotone,
} from "react-icons/pi";
import { CaveatBanner, EmptyState, MetricCard, cn } from "@steelyard/theme";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { SearchInput } from "../../components/table/search-input";
import { invoiceColumns, invoiceDefectBanner } from "./columns";
import { Filters, INVOICE_STATES } from "./filters";
import { isBr07Defect, listInvoices } from "../../lib/services/invoicing";
import { listOrders } from "../../lib/services/ordering";
import { hrefWith, readPaging } from "../../lib/paging";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";
import { Button, Text } from "rizzui";

/**
 * Ported from apps/admin/app/(app)/invoicing/page.tsx. Financial records retained eight years —
 * this list only ever grows, so `totalCount` (the API's envelope field) drives paging, never
 * `items.length`. Route-level permission gating (billing.read) happens in the root loader via
 * lib/nav.ts's NAV entry, same as every other route.
 */
const ABSENT_FROM_THE_LIST =
  "Taxable value, tax and e-way bill status aren't shown on this list. Open an invoice for its tax breakdown.";

/**
 * Every Billing `InvoiceState`, each counted by the API under the page's other filters — all six, so
 * the tiles reconcile with the header's total ("Awaiting review" is a subset of Raised, not a state).
 */
const STATE_TILES: { state: string; icon: ReactNode; tone: string; hint: string }[] = [
  { state: "raised", icon: <PiReceiptDuotone aria-hidden className="size-6" />, tone: "bg-blue", hint: "Raised, not yet registered" },
  { state: "registered", icon: <PiSealCheckDuotone aria-hidden className="size-6" />, tone: "bg-secondary", hint: "IRN registered, not yet queued" },
  { state: "registration_failed", icon: <PiWarningDuotone aria-hidden className="size-6" />, tone: "bg-red", hint: "Need a look at registration" },
  { state: "queued", icon: <PiHourglassDuotone aria-hidden className="size-6" />, tone: "bg-orange-dark", hint: "Waiting on the Tally sync" },
  { state: "posted", icon: <PiCheckCircleDuotone aria-hidden className="size-6" />, tone: "bg-green", hint: "Posted to Tally" },
  { state: "cancelled", icon: <PiXCircleDuotone aria-hidden className="size-6" />, tone: "bg-gray-500", hint: "Kept on the ledger" },
];

/** Billing takes at most this many order ids; more matches than this are said, not dropped silently. */
const ORDER_MATCH_LIMIT = 200;

/**
 * The search box matches an invoice number OR an order number. Order numbers are Ordering's, so the
 * term is matched there first (the orders list's own partial match) and Billing gets those orders'
 * ids beside the term. No order number is copied into Billing.
 */
async function ordersMatching(term: string | undefined) {
  if (!term) return undefined;
  const orders = await listOrders({ page: 1, pageSize: ORDER_MATCH_LIMIT, orderNumber: term });
  const ids = (orders?.items ?? []).map((order) => order.id).filter((id): id is string => Boolean(id));
  return { ids, capped: (orders?.totalCount ?? 0) > ids.length };
}

export async function invoiceListLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(params);
  const state = params.get("state") ?? undefined;
  const irn = params.get("irn") ?? undefined;
  const search = params.get("q") ?? undefined;
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;
  const orders = await ordersMatching(search);
  const orderIds = orders?.ids.length ? orders.ids : undefined;
  // State counts: one `pageSize: 1` read per state, under the same other filters, reading only the
  // API's own `totalCount`. A failed count costs its badge, not the list.
  // D-40: `?review=1` lists only the invoices held for a Super Admin's review.
  const review = params.get("review") === "1" ? true : undefined;
  const reviewCount = listInvoices({ page: 1, pageSize: 1, reviewPending: true })
    .then((counted) => counted.totalCount)
    .catch(() => null);
  const [result, ...stateTotals] = await Promise.all([
    listInvoices({ page, pageSize, state, irn, search, orderIds, from, to, reviewPending: review }),
    ...INVOICE_STATES.map((option) =>
      listInvoices({ page: 1, pageSize: 1, state: option.value, irn, search, orderIds, from, to })
        .then((counted) => counted.totalCount)
        .catch(() => null),
    ),
  ]);
  return { result, stateTotals, state, irn, search, ordersCapped: orders?.capped ?? false, from, to, review, reviewCount: await reviewCount };
}

export default function InvoiceListPage() {
  const { result, stateTotals, state, irn, search, ordersCapped, from, to, review, reviewCount } =
    useLoaderData() as Awaited<ReturnType<typeof invoiceListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = (over: Record<string, string | undefined>) => hrefWith("/invoicing", searchParams, over);

  const filtered = Boolean(state || irn || search || from || to || review);

  const stateOptions: StatusOption[] = [
    { label: "All states", href: query({ state: undefined, page: undefined }), active: !state },
    ...INVOICE_STATES.map((option, index) => ({
      label: option.label,
      href: query({ state: option.value, page: undefined }),
      active: option.value === state,
      count: stateTotals[index],
      status: option.value,
    })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Invoices"
        count={result.totalCount}
        description="Every invoice raised, with its state and its compliance statuses. Cancelled invoices stay listed — financial records are retained for eight years."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Invoicing" }, { name: "Invoices" }]}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* D-40: the drafts waiting on a Super Admin, first — they go nowhere until someone finalises them. */}
        <Link
          to={query({ review: review ? undefined : "1", state: undefined, page: undefined })}
          aria-current={review ? "true" : undefined}
          className={cn("rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary", review && "ring-2 ring-primary")}
        >
          <MetricCard
            title="Awaiting review"
            metric={reviewCount === null ? "Unreadable" : reviewCount.toLocaleString("en-IN")}
            icon={<PiPencilSimpleLineDuotone aria-hidden className="size-6" />}
            iconClassName="bg-orange text-white shadow-sm"
            info={<Text className="mt-0.5 text-xs text-gray-500">{review ? "Showing these — click to clear" : "Drafts to finalise"}</Text>}
            rounded="lg"
            className="h-full transition-shadow hover:shadow-md"
          />
        </Link>
        {STATE_TILES.map((tile) => {
          const index = INVOICE_STATES.findIndex((option) => option.value === tile.state);
          const count = stateTotals[index];
          const active = state === tile.state;
          return (
            <Link
              key={tile.state}
              to={query({ state: active ? undefined : tile.state, page: undefined })}
              aria-current={active ? "true" : undefined}
              className={cn("rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary", active && "ring-2 ring-primary")}
            >
              <MetricCard
                title={INVOICE_STATES[index]?.label ?? tile.state}
                metric={count === null || count === undefined ? "Unreadable" : count.toLocaleString("en-IN")}
                icon={tile.icon}
                iconClassName={cn("text-white shadow-sm", tile.tone)}
                info={<Text className="mt-0.5 text-xs text-gray-500">{active ? "Showing these — click to clear" : tile.hint}</Text>}
                rounded="lg"
                className="h-full transition-shadow hover:shadow-md"
              />
            </Link>
          );
        })}
      </div>

      {ordersCapped ? (
        <Text role="status" className="text-sm text-orange-dark">
          More than {ORDER_MATCH_LIMIT} orders match &ldquo;{search}&rdquo;, so only the first {ORDER_MATCH_LIMIT} orders&rsquo;
          invoices are listed (with every invoice whose own number matches). Type more of the number to narrow it.
        </Text>
      ) : null}

      <CaveatBanner caveat={ABSENT_FROM_THE_LIST}>
        <PagedTable
          actions={
            <>
              <Link to="/invoicing/credit-notes">
                <Button as="span" variant="outline">
                  <PiReceiptXDuotone aria-hidden className="me-1.5 size-4" />
                  Credit notes
                </Button>
              </Link>
              <Link to="/invoicing/gst-summary">
                <Button as="span" variant="outline">
                  <PiScalesDuotone aria-hidden className="me-1.5 size-4" />
                  GST summary
                </Button>
              </Link>
              <Link to="/invoicing/series">
                <Button as="span" variant="outline">
                  <PiListNumbersDuotone aria-hidden className="me-1.5 size-4" />
                  Series
                </Button>
              </Link>
            </>
          }
          result={result}
          caption="Invoices, with state, IRN status and Tally sync status"
          columns={invoiceColumns(
            can(permissionState, "billing.creditnote"),
            // The registration screen reads for billing.read as well as billing.administer (its own gate,
            // and the detail page's link) — only its register/retry/cancel buttons are administer's.
            can(permissionState, "billing.read") || can(permissionState, "billing.administer"),
          )}
          getRowId={(row) => row.id}
          status={{ label: "State", options: stateOptions }}
          rowClassName={(row) => (isBr07Defect(row) ? "border-red/40 bg-red/5 border-l-4" : undefined)}
          subRow={(row) => (isBr07Defect(row) ? invoiceDefectBanner(row) : null)}
          search={
            <SearchInput
              value={search}
              placeholder="Search by invoice or order number…"
              onSearch={(next) => navigate(query({ q: next, page: undefined }))}
            />
          }
          filters={<Filters value={{ irn, from, to }} query={query} />}
          clear={{
            active: filtered,
            href: query({ state: undefined, irn: undefined, q: undefined, from: undefined, to: undefined, review: undefined, page: undefined }),
          }}
          empty={
            <EmptyState
              title="No invoices matched"
              action={
                filtered ? (
                  <Link to="/invoicing">
                    <Button as="span" size="sm" variant="outline">
                      Clear the filters
                    </Button>
                  </Link>
                ) : null
              }
            >
              {filtered
                ? "No invoice in this period is in that state. Clearing the filters shows the whole ledger."
                : "Nothing has been invoiced yet. An invoice is raised when an order is packed."}
            </EmptyState>
          }
        />
      </CaveatBanner>
    </div>
  );
}
